// A request the browser kills because the page is unloading is not a failure.
//
// WebKit rejects every in-flight fetch with `TypeError: Load failed` while it
// tears a page down: after `beforeunload`, before `pagehide` (measured on the
// rig 2026-10-12: beforeunload +0 ms, rejection +2 ms, pagehide +6 ms; Chromium
// drops the requests without settling them). The client used to report that as
// a network failure, and a route loader awaiting it rendered the error boundary
// on the dying page (crawl backlog B10), so a Safari reload in the middle of a
// read could land in the logs as an outage.
//
// The rejection is held instead. If `pagehide` follows, the page is gone and
// the promise never settles: nothing is reported and nothing renders. If it
// does not, the unload was cancelled (Admin → Config's unsaved-changes prompt
// cancels `beforeunload`) or the failure was real, and the caller gets the
// original error after the wait. A page restored from the back/forward cache
// gets the original error for every request still held, so its screens offer a
// retry instead of spinning forever.
//
// A cancelled `beforeunload` opens the browser's leave-page dialog, which can
// stay up for as long as the operator reads it; Leave then tears the page down
// with the same rejections, long after `beforeunload` fired. No event says the
// dialog closed, but no timer runs while it is open, so a zero-delay timer set
// in `beforeunload` marks the moment it closed: until that timer runs the
// unload is still pending, and the window restarts from it.

/** How long after `beforeunload` a rejection is suspected of being the unload. */
export const UNLOAD_WINDOW_MS = 1000;
/** How long a suspected rejection waits for `pagehide` before it is thrown. */
export const PAGEHIDE_WAIT_MS = 1000;

let lastBeforeUnload = Number.NEGATIVE_INFINITY;
/** Set in `beforeunload`, cleared by a timer that cannot run while a leave-page dialog is open. */
let unloadPending = false;
let hidden = false;
const waiting = new Set<() => void>();
const held = new Set<() => void>();
const installed = new WeakSet<Window>();

/** Install the listeners once; a no-op outside a browser. */
export function installUnloadGuard(target: Window | undefined = typeof window === 'undefined' ? undefined : window): void {
  // A test's stubbed `window` may be a bare object: no listeners, no guard.
  if (!target || typeof target.addEventListener !== 'function' || installed.has(target)) return;
  installed.add(target);
  target.addEventListener('beforeunload', () => {
    lastBeforeUnload = performance.now();
    unloadPending = true;
    setTimeout(() => {
      unloadPending = false;
      lastBeforeUnload = performance.now();
    }, 0);
  });
  target.addEventListener('pagehide', () => {
    hidden = true;
    for (const wake of [...waiting]) wake();
  });
  target.addEventListener('pageshow', (event) => {
    hidden = false;
    if (!event.persisted) return;
    for (const release of [...held]) release();
  });
}

/**
 * Settles like `fail()` would, unless the page is unloading: then it waits for
 * `pagehide` and, if the page goes away, never settles.
 */
export function failUnlessUnloading(fail: () => never): Promise<never> {
  if (!hidden && !unloadPending && performance.now() - lastBeforeUnload > UNLOAD_WINDOW_MS) fail();
  return new Promise<never>((_resolve, reject) => {
    const throwIt = () => {
      try {
        fail();
      } catch (err) {
        reject(err);
      }
    };
    if (hidden) {
      holdUntilRestored(throwIt);
      return;
    }
    const wake = () => {
      clearTimeout(timer);
      waiting.delete(wake);
      holdUntilRestored(throwIt);
    };
    const timer = setTimeout(() => {
      waiting.delete(wake);
      throwIt();
    }, PAGEHIDE_WAIT_MS);
    waiting.add(wake);
  });
}

function holdUntilRestored(throwIt: () => void): void {
  const release = () => {
    held.delete(release);
    throwIt();
  };
  held.add(release);
}

/** Test seam: forget every listener-observed state. */
export function resetUnloadGuardForTests(): void {
  lastBeforeUnload = Number.NEGATIVE_INFINITY;
  unloadPending = false;
  hidden = false;
  waiting.clear();
  held.clear();
}
